import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { createLogger } from '../../utils/logger';
import { supabase } from '../../utils/supabase';
import { formatDate } from '../../utils/dateUtils';
import { 
  IoDownloadOutline, 
  IoTrashOutline, 
  IoWarningOutline, 
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoDocumentTextOutline
} from 'react-icons/io5';
import type { DataSubjectRequest } from '../../types/privacy';

const logger = createLogger({ module: 'DataRightsPortal' });

interface DataRightsPortalProps {
  className?: string;
}

export const DataRightsPortal: React.FC<DataRightsPortalProps> = ({
  className = ''
}) => {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [requests, setRequests] = useState<DataSubjectRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingExport, setSubmittingExport] = useState<boolean>(false);
  const [submittingDeletion, setSubmittingDeletion] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchRequests = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('data_subject_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setRequests(data || []);
      logger.info('Data subject requests loaded', { requestsCount: data?.length || 0 });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load requests';
      setError(errorMessage);
      logger.error('Error loading data subject requests', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [user]);

  const submitDataExportRequest = async () => {
    if (!canRequestExport()) {
      showToast('You already have a pending data export request', 'error');
      return;
    }

    setSubmittingExport(true);
    try {
      const { error } = await supabase
        .from('data_subject_requests')
        .insert({
          user_id: user?.id,
          request_type: 'access',
          status: 'pending',
          request_data: {
            requested_at: new Date().toISOString(),
            request_source: 'privacy_dashboard'
          }
        });

      if (error) throw error;

      showToast('Data export request submitted successfully', 'success');
      fetchRequests(); // Refresh the list
    } catch (error) {
      logger.error('Error submitting data export request:', error);
      showToast('Failed to submit data export request', 'error');
    } finally {
      setSubmittingExport(false);
    }
  };

  const submitAccountDeletionRequest = async () => {
    if (!canRequestDeletion()) {
      showToast('You already have a pending account deletion request', 'error');
      return;
    }

    setSubmittingDeletion(true);
    try {
      const { error } = await supabase
        .from('data_subject_requests')
        .insert({
          user_id: user?.id,
          request_type: 'deletion',
          status: 'pending',
          request_data: {
            requested_at: new Date().toISOString(),
            request_source: 'privacy_dashboard',
            confirmation: true
          }
        });

      if (error) throw error;

      showToast('Account deletion request submitted successfully', 'success');
      setShowDeleteConfirm(false);
      fetchRequests(); // Refresh the list
    } catch (error) {
      logger.error('Error submitting account deletion request:', error);
      showToast('Failed to submit account deletion request', 'error');
    } finally {
      setSubmittingDeletion(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <IoTimeOutline className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <IoTimeOutline className="w-4 h-4 text-blue-500" />;
      case 'completed':
        return <IoCheckmarkCircleOutline className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <IoWarningOutline className="w-4 h-4 text-red-500" />;
      default:
        return <IoTimeOutline className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'access': return 'Data Export';
      case 'deletion': return 'Account Deletion';
      case 'portability': return 'Data Portability';
      case 'rectification': return 'Data Correction';
      default: return type;
    }
  };

  const canRequestExport = () => {
    const recentExportRequest = requests.find(
      req => req.request_type === 'access' && 
      ['pending', 'processing'].includes(req.status)
    );
    return !recentExportRequest;
  };

  const canRequestDeletion = () => {
    const recentDeletionRequest = requests.find(
      req => req.request_type === 'deletion' && 
      ['pending', 'processing'].includes(req.status)
    );
    return !recentDeletionRequest;
  };

  if (!user) {
    return (
      <div className={`p-4 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      } ${className}`}>
        <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Please log in to access data rights options.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${
      darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
    } ${className}`}>
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Data Rights Portal
        </h3>
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Exercise your rights under GDPR and CCPA
        </p>
      </div>

      <div className="p-4 space-y-6">
        {/* Error Display */}
        {error && (
          <div className={`p-3 rounded-md border ${
            darkMode ? 'border-red-700 bg-red-900/20' : 'border-red-200 bg-red-50'
          }`}>
            <div className="flex items-center">
              <IoWarningOutline className="w-5 h-5 text-red-500 mr-2" />
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Data Export Section */}
        <div className={`p-4 rounded-lg border ${
          darkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <IoDownloadOutline className="w-5 h-5 text-blue-500 mr-2" />
                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Export Your Data
                </h4>
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Download a copy of all your personal data stored in our system. This includes your profile, 
                career cards, notes, reviews, and activity history.
              </p>
              <ul className={`text-xs mt-2 space-y-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <li>• Data will be provided in JSON format</li>
                <li>• Processing typically takes 24-48 hours</li>
                <li>• Download link will be sent to your email</li>
              </ul>
            </div>
            <button
              onClick={submitDataExportRequest}
              disabled={!canRequestExport() || submittingExport}
              className={`ml-4 px-4 py-2 rounded-md text-sm font-medium ${
                canRequestExport() && !submittingExport
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submittingExport ? 'Submitting...' : 'Request Export'}
            </button>
          </div>
        </div>

        {/* Account Deletion Section */}
        <div className={`p-4 rounded-lg border ${
          darkMode ? 'border-red-600 bg-red-900/10' : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <IoTrashOutline className="w-5 h-5 text-red-500 mr-2" />
                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Delete Your Account
                </h4>
              </div>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <ul className={`text-xs mt-2 space-y-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                <li>• All your data will be permanently deleted</li>
                <li>• Account deletion takes effect immediately</li>
                <li>• You will receive a confirmation email</li>
              </ul>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!canRequestDeletion() || submittingDeletion}
              className={`ml-4 px-4 py-2 rounded-md text-sm font-medium ${
                canRequestDeletion() && !submittingDeletion
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submittingDeletion ? 'Submitting...' : 'Delete Account'}
            </button>
          </div>
        </div>

        {/* Request History */}
        {requests.length > 0 && (
          <div>
            <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Request History
            </h4>
            <div className="space-y-3">
              {requests.map((request) => (
                <div key={request.id} className={`p-3 rounded-md border ${
                  darkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(request.status)}
                      <span className={`ml-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {getRequestTypeLabel(request.request_type)}
                      </span>
                      <div className="mt-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Status: {getStatusLabel(request.status)}
                        </span>
                      </div>
                    </div>
                    <time className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatDate(request.created_at)}
                    </time>
                  </div>
                  
                  {request.response_data?.download_url && request.status === 'completed' && (
                    <div className="mt-2">
                      <a
                        href={request.response_data.download_url}
                        className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600"
                        download
                      >
                        <IoDownloadOutline className="w-4 h-4 mr-1" />
                        Download Data
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`max-w-md w-full mx-4 rounded-lg ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <IoWarningOutline className="w-6 h-6 text-red-500 mr-3" />
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Confirm Account Deletion
                </h3>
              </div>
              
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete your account? This action will:
              </p>
              
              <ul className={`text-sm space-y-1 mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li>• Permanently delete all your career cards, notes, and reviews</li>
                <li>• Remove your profile and account information</li>
                <li>• Cancel any active subscriptions</li>
                <li>• Cannot be undone or recovered</li>
              </ul>
              
              <div className="flex space-x-3">
                <button
                  onClick={submitAccountDeletionRequest}
                  disabled={submittingDeletion}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {submittingDeletion ? 'Submitting...' : 'Yes, Delete My Account'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className={`flex-1 px-4 py-2 rounded-md border ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 